import { useCallback, useEffect, useMemo, useState } from "react";
import { createEvent, deleteEvent, listEvents, upsertEvents } from "../api/events";
import { computeSchedule, calculateTotals } from "../lib/calc";
import { formatDateTime } from "../lib/time";

const EMPTY_EVENT = {
  line: "",
  operation: "",
  estimatedHours: "",
  actualHours: "",
  remarks: "",
};

// Diff-based upsert: only write events whose schedule actually changed
const getChangedEvents = (oldEvents, newEvents, editedId = null) =>
  newEvents.filter((newEvent, i) => {
    const oldEvent = oldEvents[i];
    if (!oldEvent) return true;
    if (newEvent.id === editedId) return true;
    return newEvent.startAt !== oldEvent.startAt || newEvent.endAt !== oldEvent.endAt;
  });

const useEvents = (projectId, userId, projectStartAt) => {
  const [events, setEvents] = useState([]);
  const [newEvent, setNewEvent] = useState(EMPTY_EVENT);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!projectId) {
      setEvents([]);
      return;
    }

    let cancelled = false;
    const load = async () => {
      try {
        const data = await listEvents(projectId);
        if (!cancelled) setEvents(computeSchedule(projectStartAt, data));
      } catch (err) {
        if (!cancelled) setError(err.message || "Failed to load events");
      }
    };
    load();
    return () => { cancelled = true; };
  }, [projectId, projectStartAt]);

  const totals = useMemo(() => calculateTotals(events), [events]);

  const estimatedCompletion = useMemo(() => {
    const last = events[events.length - 1];
    return last?.endAt ? formatDateTime(last.endAt) : "";
  }, [events]);

  // Recompute schedule and persist changed events after project save
  const recomputeAndPersist = useCallback(async (newStartAt) => {
    if (!events.length) return;
    const recomputed = computeSchedule(newStartAt, events);
    const changed = getChangedEvents(events, recomputed);
    setEvents(recomputed);
    if (changed.length) {
      await upsertEvents(changed, projectId, userId);
    }
  }, [events, projectId, userId]);

  const handleAddEvent = useCallback(async () => {
    if (!projectId || !projectStartAt) {
      setError("Set a project start date/time before adding events.");
      return;
    }

    const sortIndex = events.length
      ? Math.max(...events.map((e) => e.sortIndex || 0)) + 1
      : 0;

    const draft = { ...newEvent, sortIndex };
    const computed = computeSchedule(projectStartAt, [...events, draft]).slice(-1)[0];

    try {
      await createEvent({ projectId, userId, event: computed });
      const refreshed = await listEvents(projectId);
      setEvents(computeSchedule(projectStartAt, refreshed));
      setNewEvent(EMPTY_EVENT);
    } catch (err) {
      setError(err.message || "Failed to add event");
    }
  }, [projectId, userId, projectStartAt, events, newEvent]);

  const handleDeleteEvent = useCallback(async (eventId) => {
    try {
      await deleteEvent(eventId);
      const remaining = events.filter((e) => e.id !== eventId);
      const recomputed = computeSchedule(projectStartAt, remaining);
      const changed = getChangedEvents(
        events.filter((e) => e.id !== eventId),
        recomputed
      );
      setEvents(recomputed);
      if (changed.length) {
        await upsertEvents(changed, projectId, userId);
      }
    } catch (err) {
      setError(err.message || "Failed to delete event");
    }
  }, [events, projectStartAt, projectId, userId]);

  return {
    events,
    setEvents,
    newEvent,
    setNewEvent,
    totals,
    estimatedCompletion,
    error,
    setError,
    handleAddEvent,
    handleDeleteEvent,
    recomputeAndPersist,
    EMPTY_EVENT,
  };
};

export default useEvents;
